import type { ObjectId } from "@novorender/api";
import type { ReadonlyVec3 } from "gl-matrix";
import { vec3 } from "gl-matrix";
import { useCallback } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { type Coordinate, coordinatesActions, selectCoordinates } from "features/coordinates/coordinatesSlice";
import { selectMeasure, selectMeasurePickSettings } from "features/measure";
import { selectCurrentPickerGeneratedParametricData } from "features/render";
import { getSnapGuides, getSnapInfo } from "features/render/measureSnap";
import { applyCameraDistanceToMeasureTolerance } from "features/render/utils";
import { telemetry } from "telemetry";

export function useAddCoordinate() {
    const {
        state: { view },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const measure = useAppSelector(selectMeasure);
    const measurePickSettings = useAppSelector(selectMeasurePickSettings);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const coordinates = useAppSelector(selectCoordinates);

    return useCallback(
        async (
            position: ReadonlyVec3,
            {
                screenOffset = { x: 0, y: 0 },
                coordinateId,
                objectId,
                normal,
            }: {
                screenOffset?: { x: number; y: number };
                coordinateId: string;
                objectId: ObjectId;
                normal: ReadonlyVec3 | undefined;
            },
        ) => {
            try {
                let point: { position: ReadonlyVec3; guides: vec3[]; snapType?: string } = {
                    position,
                    guides: [],
                    snapType: "normal",
                };

                if (measure.hover?.connectionPoint) {
                    if (!view) {
                        return false;
                    }

                    const tolerance = applyCameraDistanceToMeasureTolerance(
                        position,
                        view.renderState.camera,
                        measurePickSettings,
                    );
                    const entity = await view.measure?.core.pickMeasureEntity(
                        objectId,
                        measure.hover.connectionPoint,
                        tolerance,
                        allowGeneratedParametric.enabled,
                    );
                    const isArcCenter =
                        entity && "curvePart" in entity.entity && entity.entity.curvePart === "arcCenter";
                    const snapInfo =
                        entity?.entity &&
                        entity.connectionPoint &&
                        (await getSnapInfo({
                            view,
                            entity: entity.entity,
                            connectionPoint: entity.connectionPoint,
                            corners: true,
                        }));
                    point = {
                        position: snapInfo?.type === "midpoint" ? snapInfo.connection : measure.hover.connectionPoint,
                        guides: snapInfo ? getSnapGuides(snapInfo) : [],
                        snapType: isArcCenter ? "normal" : snapInfo?.type,
                    };
                }

                if (coordinates.some((c) => vec3.dist(point.position, c.point.position) < 1e-6)) {
                    return false;
                }

                const coordinate: Coordinate = {
                    point,
                    id: coordinateId,
                    objectId,
                    normal,
                    screenOffset,
                };
                dispatch(coordinatesActions.addCoordinate(coordinate));
                dispatchLastPickSample(lastPickSampleActions.set(null));
                return coordinate;
            } catch (error) {
                console.error("Error adding coordinate:", error);
                telemetry.trackCaughtException({
                    source: "coordinates.useAddCoordinate.addCoordinate",
                    category: "render",
                    error,
                    severity: "error",
                });
                return false;
            }
        },
        [
            allowGeneratedParametric,
            dispatch,
            dispatchLastPickSample,
            measure.hover,
            measurePickSettings,
            view,
            coordinates,
        ],
    );
}
